import { format, parse } from 'date-fns';
import { parseTimeOnDay } from '../company-hours/companyHoursBounds';

export const WEEKDAY_KEYS = [
  'monday',
  'tuesday',
  'wednesday',
  'thursday',
  'friday',
  'saturday',
  'sunday',
];

const DEFAULT_DAY_TEMPLATE = {
  isWorking: false,
  start: '09:00',
  end: '17:00',
  hasBreak: false,
  breakStart: '12:00',
  breakEnd: '13:00',
};

const EMPTY_BREAKS = [];

const clampTimeValue = (value, fallback) => {
  const raw = String(value || '').trim();
  return /^\d{2}:\d{2}$/.test(raw) ? raw : fallback;
};

const toDateFrom24HourTime = (date, value) => {
  return parse(clampTimeValue(value, '00:00'), 'HH:mm', date);
};

const toDateFromCompanyTime = (date, value) => parseTimeOnDay(date, value);
const normalizeDay = (dayConfig = {}, fallbackDay = DEFAULT_DAY_TEMPLATE) => {
  const nextDay = {
    isWorking: Boolean(dayConfig?.isWorking ?? fallbackDay.isWorking),
    start: clampTimeValue(dayConfig?.start, fallbackDay.start),
    end: clampTimeValue(dayConfig?.end, fallbackDay.end),
    hasBreak: Boolean(dayConfig?.hasBreak ?? fallbackDay.hasBreak),
    breakStart: clampTimeValue(dayConfig?.breakStart, fallbackDay.breakStart),
    breakEnd: clampTimeValue(dayConfig?.breakEnd, fallbackDay.breakEnd),
  };

  if (!nextDay.isWorking) {
    return {
      ...nextDay,
      hasBreak: false,
    };
  }

  const start = toDateFrom24HourTime(new Date(), nextDay.start);
  const end = toDateFrom24HourTime(new Date(), nextDay.end);
  if (end <= start) {
    nextDay.end = fallbackDay.end;
  }

  if (nextDay.hasBreak) {
    const breakStart = toDateFrom24HourTime(new Date(), nextDay.breakStart);
    const breakEnd = toDateFrom24HourTime(new Date(), nextDay.breakEnd);
    if (breakEnd <= breakStart) {
      nextDay.hasBreak = false;
    }
  }

  return nextDay;
};

const mergeIntervals = (intervals) => {
  if (!intervals.length) return [];

  const sorted = [...intervals].sort((a, b) => a.start - b.start);
  const merged = [sorted[0]];

  for (let i = 1; i < sorted.length; i += 1) {
    const current = sorted[i];
    const previous = merged[merged.length - 1];

    if (current.start <= previous.end) {
      previous.end = new Date(Math.max(previous.end.getTime(), current.end.getTime()));
    } else {
      merged.push(current);
    }
  }

  return merged;
};

export const buildDefaultWorkingSchedule = (companyHours = {}) => {
  return WEEKDAY_KEYS.reduce((schedule, dayKey) => {
    const companyDay = companyHours?.[dayKey];

    if (!companyDay?.isOpen) {
      schedule[dayKey] = {
        ...DEFAULT_DAY_TEMPLATE,
        isWorking: false,
      };
      return schedule;
    }

    const normalizedDay = {
      isWorking: true,
      start: format(toDateFromCompanyTime(new Date(), companyDay.open) || toDateFrom24HourTime(new Date(), DEFAULT_DAY_TEMPLATE.start), 'HH:mm'),
      end: format(toDateFromCompanyTime(new Date(), companyDay.close) || toDateFrom24HourTime(new Date(), DEFAULT_DAY_TEMPLATE.end), 'HH:mm'),
      hasBreak: Boolean(companyDay.hasBreak && companyDay.breakStart && companyDay.breakEnd),
      breakStart: companyDay.hasBreak && companyDay.breakStart
        ? format(toDateFromCompanyTime(new Date(), companyDay.breakStart), 'HH:mm')
        : DEFAULT_DAY_TEMPLATE.breakStart,
      breakEnd: companyDay.hasBreak && companyDay.breakEnd
        ? format(toDateFromCompanyTime(new Date(), companyDay.breakEnd), 'HH:mm')
        : DEFAULT_DAY_TEMPLATE.breakEnd,
    };

    schedule[dayKey] = normalizeDay(normalizedDay);
    return schedule;
  }, {});
};

export const normalizeWorkingSchedule = (workingSchedule, companyHours = {}) => {
  const fallbackSchedule = buildDefaultWorkingSchedule(companyHours);

  return WEEKDAY_KEYS.reduce((schedule, dayKey) => {
    schedule[dayKey] = normalizeDay(workingSchedule?.[dayKey], fallbackSchedule[dayKey]);
    return schedule;
  }, {});
};

export const getDayKeyFromDate = (date) => {
  return format(date, 'EEEE').toLowerCase();
};

export const getWorkingScheduleForDate = (workingSchedule, date, companyHours = {}) => {
  const normalizedSchedule = normalizeWorkingSchedule(workingSchedule, companyHours);
  return normalizedSchedule[getDayKeyFromDate(date)];
};

const subtractBreaksFromWindow = ({ start, end, breaks }) => {
  let intervals = [{ start, end }];

  breaks.forEach((currentBreak) => {
    intervals = intervals.flatMap((interval) => {
      const overlaps =
        currentBreak.start < interval.end &&
        currentBreak.end > interval.start;

      if (!overlaps) return [interval];

      const nextIntervals = [];

      if (currentBreak.start > interval.start) {
        nextIntervals.push({
          start: interval.start,
          end: currentBreak.start,
        });
      }

      if (currentBreak.end < interval.end) {
        nextIntervals.push({
          start: currentBreak.end,
          end: interval.end,
        });
      }

      return nextIntervals;
    });
  });

  return intervals.filter((interval) => interval.end > interval.start);
};

export const getEffectiveScheduleForDate = ({ workingSchedule, date, companyHours = {} }) => {
  const dayKey = getDayKeyFromDate(date);
  const companyDay = companyHours?.[dayKey];
  const rawEmployeeDay = workingSchedule?.[dayKey];
  const employeeExplicitlyOff = rawEmployeeDay?.isWorking === false;
  const employeeDay = getWorkingScheduleForDate(workingSchedule, date, companyHours);

  if (!companyDay?.isOpen || employeeExplicitlyOff) {
    return {
      dayKey,
      isWorking: false,
      start: null,
      end: null,
      breaks: EMPTY_BREAKS,
      intervals: EMPTY_BREAKS,
    };
  }

  const companyStart = toDateFromCompanyTime(date, companyDay.open);
  const companyEnd = toDateFromCompanyTime(date, companyDay.close);

  if (!companyStart || !companyEnd || !(companyEnd > companyStart)) {
    return {
      dayKey,
      isWorking: false,
      start: null,
      end: null,
      breaks: EMPTY_BREAKS,
      intervals: EMPTY_BREAKS,
    };
  }

  const useCompanyWindow = !employeeDay?.isWorking;
  const employeeStart = useCompanyWindow
    ? companyStart
    : toDateFrom24HourTime(date, employeeDay.start);
  const employeeEnd = useCompanyWindow
    ? companyEnd
    : toDateFrom24HourTime(date, employeeDay.end);

  const start = useCompanyWindow
    ? companyStart
    : new Date(Math.max(companyStart.getTime(), employeeStart.getTime()));
  const end = useCompanyWindow
    ? companyEnd
    : new Date(Math.min(companyEnd.getTime(), employeeEnd.getTime()));

  if (!(end > start)) {
    return {
      dayKey,
      isWorking: false,
      start: null,
      end: null,
      breaks: EMPTY_BREAKS,
      intervals: EMPTY_BREAKS,
    };
  }

  const breakIntervals = [];

  if (companyDay.hasBreak && companyDay.breakStart && companyDay.breakEnd) {
    const breakStart = toDateFromCompanyTime(date, companyDay.breakStart);
    const breakEnd = toDateFromCompanyTime(date, companyDay.breakEnd);
    if (breakEnd > breakStart) {
      breakIntervals.push({ start: breakStart, end: breakEnd });
    }
  }

  if (employeeDay.hasBreak && !useCompanyWindow) {
    const breakStart = toDateFrom24HourTime(date, employeeDay.breakStart);
    const breakEnd = toDateFrom24HourTime(date, employeeDay.breakEnd);
    if (breakEnd > breakStart) {
      breakIntervals.push({ start: breakStart, end: breakEnd });
    }
  }

  const clippedBreaks = mergeIntervals(
    breakIntervals
      .map((currentBreak) => ({
        start: new Date(Math.max(currentBreak.start.getTime(), start.getTime())),
        end: new Date(Math.min(currentBreak.end.getTime(), end.getTime())),
      }))
      .filter((currentBreak) => currentBreak.end > currentBreak.start)
  );

  return {
    dayKey,
    isWorking: true,
    start,
    end,
    breaks: clippedBreaks,
    intervals: subtractBreaksFromWindow({ start, end, breaks: clippedBreaks }),
  };
};

export const employeeHasAvailabilityOnDate = ({
  workingSchedule,
  date,
  companyHours = {},
  totalDurationMinutes = 0,
}) => {
  const effectiveSchedule = getEffectiveScheduleForDate({ workingSchedule, date, companyHours });
  if (!effectiveSchedule.isWorking) return false;

  if (!totalDurationMinutes || totalDurationMinutes <= 0) {
    return effectiveSchedule.intervals.length > 0;
  }

  return effectiveSchedule.intervals.some((interval) => {
    const durationMs = interval.end.getTime() - interval.start.getTime();
    return durationMs >= totalDurationMinutes * 60 * 1000;
  });
};

export const slotFitsEmployeeSchedule = ({
  workingSchedule,
  date,
  slotStart,
  slotEnd,
  companyHours = {},
}) => {
  const effectiveSchedule = getEffectiveScheduleForDate({ workingSchedule, date, companyHours });
  if (!effectiveSchedule.isWorking) return false;

  return effectiveSchedule.intervals.some((interval) => (
    slotStart >= interval.start &&
    slotEnd <= interval.end
  ));
};

export const formatWorkingScheduleTime = (value) => {
  if (!value) return '';
  return format(toDateFrom24HourTime(new Date(), value), 'h:mm a');
};

export const formatWorkingScheduleLabel = (dayConfig) => {
  if (!dayConfig?.isWorking) return 'Off';

  const scheduleLabel = `${formatWorkingScheduleTime(dayConfig.start)} - ${formatWorkingScheduleTime(dayConfig.end)}`;
  if (!dayConfig.hasBreak) return scheduleLabel;
  return `${scheduleLabel} (Break ${formatWorkingScheduleTime(dayConfig.breakStart)} - ${formatWorkingScheduleTime(dayConfig.breakEnd)})`;
};
